'use client';

import React, { useEffect, useRef, useState } from 'react';
import Link from 'next/link';
import AppImage from '@/components/ui/AppImage';
import { useLanguage } from '@/context/LanguageContext';

// key = índice de las claves de texto products.cat.N / products.tag.N en LanguageContext.
// Mismo orden que las 6 líneas del catálogo Rev. 7. Filas: mar (8) + cordero (4) · aceite (4) +
// quinoa, chía y sésamo (8) · café (6) + azúcares (6).
// Fotos: dominio público / CC0.
const categoryData = [
  {
    id: 'seafood',
    key: 0,
    image: "/assets/images/rocket_gen_img_10c75a36d-1773234368179.webp",
    alt: 'Fresh fish and seafood catch with animal protein products in industrial facility',
    span: 'large',
    items: {
      es: ['Langostino HOSO, HLSO y P&D', 'Merluza y merluza negra', 'Calamar illex', 'Vieira patagónica', 'Pulpo', 'Pez espada y albacora'],
      en: ['Red shrimp HOSO, HLSO & P&D', 'Hake & toothfish', 'Illex squid', 'Patagonian scallop', 'Octopus', 'Swordfish & albacore'],
    },
  },
  {
    id: 'lamb',
    key: 1,
    image: "/assets/images/cordero-patagonico.webp",
    alt: 'Flock of sheep raised on open Patagonian grassland',
    span: 'medium',
    items: {
      es: ['Carcasas, cortes con hueso y deshuesados', 'Zafra octubre a enero', 'Halal o Kosher según planta y destino', 'Certificado por lote'],
      en: ['Carcasses, bone-in and boneless cuts', 'Season October to January', 'Halal or Kosher by plant and destination', 'Lot certificate'],
    },
  },
  {
    id: 'oils',
    key: 7,
    image: "/assets/images/aceite-oliva.webp",
    alt: 'Bottles of extra virgin olive oil',
    span: 'medium',
    items: {
      es: ['Virgen extra a granel', 'Envasado, desde un pallet', 'Marca de la finca o marca propia', 'Planta con HACCP certificado'],
      en: ['Bulk extra virgin', 'Bottled, from one pallet', "Estate brand or buyer's private label", 'Plant with certified HACCP'],
    },
  },
  {
    id: 'quinoa',
    key: 6,
    image: "/assets/images/quinoa-sesamo.webp",
    alt: 'Quinoa plants in the Andean highlands and a sesame crop in flower (quinoa, chia and sesame line)',
    span: 'large',
    items: {
      es: ['Quinoa blanca · Bolivia', 'Bolsa de 25 kg o big bag', 'Otras variedades a consulta', 'Chía negra o blanca · Paraguay', 'Orgánica o convencional', 'Sésamo blanco natural', 'Pureza 99,90 % mín. · humedad 6 % máx.', 'Por contenedor, ~25 t'],
      en: ['White quinoa · Bolivia', '25 kg bags or big bags', 'Other varieties on request', 'Black or white chia · Paraguay', 'Organic or conventional', 'Natural white sesame', 'Purity 99.90% min. · moisture 6% max.', 'By container, ~25 t'],
    },
  },
  {
    id: 'infusions',
    key: 3,
    image: "/assets/images/unsplash-1677125683463.webp",
    alt: 'Coffee beans',
    span: 'half',
    items: {
      es: ['Café verde, comercial y de especialidad', 'Café tostado, en grano o molido', 'Trazabilidad al productor'],
      en: ['Green coffee, commercial and specialty', 'Roasted coffee, whole bean or ground', 'Traceable to the grower'],
    },
  },
  {
    id: 'sugar',
    key: 4,
    image: "/assets/images/rocket_gen_img_1e088f9c7-1772541951004.webp",
    alt: 'Raw sugar cane stalks and refined sugar crystals in industrial setting',
    span: 'half',
    items: {
      es: ['Blanca refinada ICUMSA 45', 'Crudo y demerara', 'Rubia y mascabo'],
      en: ['Refined white ICUMSA 45', 'Raw and demerara', 'Light brown and muscovado'],
    },
  },
];

export default function ProductsGrid() {
  const [activeFilter, setActiveFilter] = useState('All');
  const sectionRef = useRef<HTMLDivElement>(null);
  const { t, lang } = useLanguage();

  const filterTabs = [
    { id: 'All', label: t('products.grid.filter.all') },
    ...categoryData?.map((c) => ({ id: c?.id, label: t(`products.tag.${c?.key}`) })),
  ];

  useEffect(() => {
    const reveals = sectionRef?.current?.querySelectorAll('.reveal');
    if (!reveals) return;
    const observer = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.remove('hidden-init'); }),
      { threshold: 0.06 }
    );
    reveals?.forEach((el) => observer?.observe(el));
    return () => observer?.disconnect();
  }, []);

  const filtered = activeFilter === 'All'
    ? categoryData
    : categoryData?.filter((c) => c?.id === activeFilter);

  return (
    <section className="py-24 bg-white" ref={sectionRef}>
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-8 mb-12">
          <div>
            <span className="section-label block mb-3 reveal hidden-init">{t('products.grid.label')}</span>
            <h2 className="font-display font-bold text-primary reveal hidden-init stagger-1" style={{ fontSize: 'clamp(28px, 3.5vw, 44px)', letterSpacing: '-0.025em', lineHeight: '1.1' }}>
              {t('products.grid.title1')}
              <br />
              <span className="italic font-light text-secondary">{t('products.grid.titleItalic')}</span>
            </h2>
          </div>

          <div className="flex flex-wrap gap-2 reveal hidden-init stagger-2">
            {filterTabs?.map((tab) => (
              <button key={tab?.id} onClick={() => setActiveFilter(tab?.id)} className={`px-4 py-2 rounded-lg text-sm font-semibold transition-all duration-200 ${activeFilter === tab?.id ? 'bg-primary text-white shadow-sm' : 'bg-brand-bgDark text-brand-muted hover:bg-brand-border hover:text-brand-fg'}`}>
                {tab?.label}
              </button>
            ))}
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-5">
          {filtered?.map((cat, i) => {
            const isLarge = cat?.span === 'large' || filtered?.length === 1;
            const colSpan = isLarge ? 'md:col-span-8' : cat?.span === 'half' ? 'md:col-span-6' : 'md:col-span-4';

            return (
              <div key={cat?.id} className={`product-card ${colSpan} reveal hidden-init stagger-${Math.min(i + 1, 4)}`} style={{ minHeight: isLarge ? '480px' : '420px' }}>
                <AppImage src={cat?.image} alt={cat?.alt} fill className="object-cover" />
                <div className="overlay" />
                <div className="absolute inset-0 z-10 p-6 md:p-8 flex flex-col justify-end">
                  <span className="self-start mb-3 px-3 py-1 rounded-full text-xs font-semibold uppercase tracking-wider" style={{ background: 'rgba(26,122,138,0.25)', border: '1px solid rgba(26,122,138,0.4)', color: '#2A9BAD' }}>{t(`products.tag.${cat?.key}`)}</span>
                  <h3 className="font-display font-bold text-white mb-2" style={{ fontSize: isLarge ? '28px' : '22px', letterSpacing: '-0.02em' }}>{t(`products.cat.${cat?.key}.name`)}</h3>
                  <p className="text-white/65 text-sm leading-relaxed mb-4 max-w-sm">{t(`products.cat.${cat?.key}.description`)}</p>
                  <div className="flex flex-wrap gap-2">
                    {(cat?.items?.[lang] ?? cat?.items?.en)?.slice(0, isLarge ? 6 : 4)?.map((item) => (
                      <span key={item} className="px-2.5 py-1 rounded-md text-xs font-medium" style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)', color: 'rgba(255,255,255,0.8)' }}>{item}</span>
                    ))}
                  </div>
                  <Link href="/contact" className="mt-5 self-start flex items-center gap-2 text-sm font-semibold text-secondary-bright hover:text-white transition-colors">
                    {t('products.grid.inquire')}
                    <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24"><path d="M5 12h14M12 5l7 7-7 7" /></svg>
                  </Link>
                </div>
              </div>
            );
          })}
        </div>

        <div className="mt-10 text-center reveal hidden-init">
          <p className="text-brand-muted text-sm">
            {t('products.grid.note')}{' '}
            <Link href="/contact" className="text-secondary font-semibold hover:underline">{t('products.grid.contact')}</Link>{' '}
            {t('products.grid.noteLink')}
          </p>
          <p className="text-brand-muted text-xs mt-3 opacity-70">
            {t('products.grid.credits')}
          </p>
        </div>
      </div>
    </section>
  );
}